import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useRoster, useCreateRosterAssignment, usePatchRosterAssignment, useDeleteRosterAssignment
} from '../hooks/useRoster'
import { useEventRoles, useSetEventRoles } from '../hooks/useRoles'
import { useVolunteerOffers, useReviewVolunteerOffer, useConfirmVolunteer, useConfirmRole } from '../hooks/useVolunteerLoop'
import { EvIcon as Icon } from './volunteer/EvIcon'
import { ModalShell, CancelButton, PrimaryButton, ServerError } from './modal-form'
import { parseIsoDate } from '../lib/dates'
import { useMe } from '../hooks/useMe'
import { useArchived } from '../contexts/ArchiveContext'
import type { EventListItem, LibraryRole, RosterAssignment } from '../types'
import {
  EV, EvAvatar, StatusDot, firstName
} from './volunteer/evKit'
import {
  AvailabilityView, AssignmentsView, CoverageView, SwapsView, ByDayView, ByPersonView,
  type RosterCtx, type VolunteerLite
} from './volunteer/rosterViews'

// RosterTab — the rebuilt per-event Roster (BUILD_PLAN PR E-2). Six sub-tabs
// (Availability · Assignments · Coverage · Swaps & cover · By day · By person)
// with desktop drag-and-drop. Management is desktop + admin only; below the
// 920px breakpoint, or for non-admins, the grid is read-only with a notice.
// The CSV + print export from the previous build is preserved verbatim.

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const SUBTABS = [
  { key: 'availability', label: 'Availability', icon: 'bell', hint: 'Offers coming in from InfoHub — review, approve and place' },
  { key: 'assignments', label: 'Assignments', icon: 'grid', hint: 'The role × day grid — drag a person onto a role to give them that role (people can hold several). Hold Shift while dropping to move them instead.' },
  { key: 'coverage', label: 'Coverage', icon: 'layers', hint: 'Where the gaps are, at a glance' },
  { key: 'swaps', label: 'Swaps & cover', icon: 'swap', hint: 'Drop-outs, cancellations and cover — find a replacement fast' },
  { key: 'byday', label: 'By day', icon: 'calendar', hint: 'One day at a time — less scrolling on a busy event' },
  { key: 'byperson', label: 'By person', icon: 'user', hint: "One volunteer's whole event in a single view" },
] as const
type SubKey = typeof SUBTABS[number]['key']

function useIsDesktop(): boolean {
  const query = '(min-width: 921px)'
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : true)
  useEffect(() => {
    const mq = window.matchMedia(query)
    const fn = () => setIsDesktop(mq.matches)
    mq.addEventListener('change', fn)
    return () => mq.removeEventListener('change', fn)
  }, [])
  return isDesktop
}

interface Props { event: EventListItem }

export function RosterTab({ event }: Props) {
  const eventId = event.id
  const archived = useArchived()
  const { data: me } = useMe()
  const isAdmin = !!me?.isAdmin
  const isDesktop = useIsDesktop()
  const fullManager = isDesktop && isAdmin

  const { data, isLoading, error } = useRoster(eventId)
  const offersQ = useVolunteerOffers(fullManager ? eventId : 0)
  const [configureOpen, setConfigureOpen] = useState(false)
  const [sub, setSub] = useState<SubKey>('availability')

  // Mutations (shared by every view via ctx.actions).
  const createMut = useCreateRosterAssignment(eventId)
  const patchMut = usePatchRosterAssignment(eventId)
  const deleteMut = useDeleteRosterAssignment(eventId)
  const reviewMut = useReviewVolunteerOffer(eventId)
  const confirmVolMut = useConfirmVolunteer(eventId)
  const confirmRoleMut = useConfirmRole(eventId)

  const offers = offersQ.data ?? []

  // Volunteer universe: everyone who offered (richest info) plus anyone already
  // assigned. Pool = offerers; involved = anyone with an offer or an assignment.
  const volunteers = useMemo(() => {
    const m = new Map<number, VolunteerLite>()
    const days = data?.days ?? []
    for (const o of offers) {
      m.set(o.userId, {
        id: o.userId,
        name: o.volunteer.name,
        initials: o.volunteer.avatarInitials,
        canDo: o.roleIds,
        availDays: o.mode === 'whole' ? days : o.days,
        phone: o.volunteer.phone,
        offerStatus: o.status
      })
    }
    if (data) {
      for (const a of data.assignments) {
        if (!m.has(a.userId)) {
          m.set(a.userId, { id: a.userId, name: a.userName, initials: a.userAvatarInitials, canDo: [], availDays: [] })
        }
      }
    }
    return m
  }, [offers, data])

  const byCellMap = useMemo(() => {
    const m = new Map<string, RosterAssignment[]>()
    if (!data) return m
    for (const a of data.assignments) {
      const key = `${a.roleId}|${a.assignmentDate}`
      const list = m.get(key) ?? []
      list.push(a)
      m.set(key, list)
    }
    return m
  }, [data])

  if (isLoading) {
    return <div className="text-ink-soft text-sm font-mono">Loading roster…</div>
  }
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load roster</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }
  if (!data) return null

  const { roles, days } = data
  const rolesById = new Map(roles.map((r) => [r.id, r]))
  const signedOnSet = new Set(data.signedOnUserIds)

  // Anyone placed but not yet confirmed. Confirming is a separate step from
  // approving an offer and is easy to miss — this drives the prominent
  // "Confirm & notify all" banner so it can't be.
  const assignedAssignments = data.assignments.filter((a) => a.status === 'assigned')
  const assignedUserIds = Array.from(new Set(assignedAssignments.map((a) => a.userId)))
  const confirmAllAssigned = () => { for (const uid of assignedUserIds) confirmVolMut.mutate(uid) }

  const ctx: RosterCtx = {
    eventId,
    roles,
    days,
    assignments: data.assignments,
    offers,
    byCell: (roleId, date) => byCellMap.get(`${roleId}|${date}`) ?? [],
    forVol: (userId) => data.assignments.filter((a) => a.userId === userId),
    vol: (userId) => volunteers.get(userId) ?? { id: userId, name: '?', initials: '?', canDo: [], availDays: [] },
    role: (roleId) => rolesById.get(roleId),
    pool: offers.map((o) => volunteers.get(o.userId)!).filter(Boolean),
    involved: Array.from(volunteers.values()),
    signedOn: (userId) => signedOnSet.has(userId),
    isAdmin,
    archived,
    actions: {
      assign: (userId, roleId, date) => createMut.mutate({ userId, roleId, assignmentDate: date }),
      move: (assignmentId, roleId, date) => patchMut.mutate({ id: assignmentId, body: { roleId, assignmentDate: date } }),
      unassign: (assignmentId) => deleteMut.mutate(assignmentId),
      confirmChip: (assignmentId) => patchMut.mutate({ id: assignmentId, body: { status: 'confirmed' } }),
      confirmVolunteer: (userId) => confirmVolMut.mutate(userId),
      confirmAllAssigned,
      confirmRole: (roleId) => confirmRoleMut.mutate(roleId),
      reviewOffer: (offerId, decision) => reviewMut.mutate({ offerId, decision })
    }
  }

  const cur = SUBTABS.find((t) => t.key === sub) ?? SUBTABS[0]
  const Views: Record<SubKey, (p: { ctx: RosterCtx }) => JSX.Element> = {
    availability: AvailabilityView,
    assignments: AssignmentsView,
    coverage: CoverageView,
    swaps: SwapsView,
    byday: ByDayView,
    byperson: ByPersonView
  }
  const View = Views[sub]

  return (
    <div className="space-y-4" data-roster-tab>
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap" data-print-hide>
        <div>
          <h2 className="font-display text-xl">Roster</h2>
          <p className="text-sm text-ink-soft mt-0.5">
            Volunteers offer availability from InfoHub; place and confirm them here.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/events/${eventId}/availability`}
            className="px-3 py-1.5 text-xs font-semibold rounded border border-line bg-white hover:bg-parchment transition-colors"
          >
            Mark availability
          </Link>
          <button
            type="button"
            onClick={() => exportRosterCsv(event, data.roles, data.days, data.assignments)}
            disabled={data.assignments.length === 0}
            className="px-3 py-1.5 text-xs font-semibold rounded border border-line bg-white hover:bg-parchment transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={data.assignments.length === 0 ? 'Assign at least one cell first' : 'Export the roster as CSV (opens in Excel)'}
          >
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="px-3 py-1.5 text-xs font-semibold rounded border border-line bg-white hover:bg-parchment transition-colors"
            title="Print the roster (A4 landscape)"
          >
            Print
          </button>
          {isAdmin && !archived && (
            <button
              type="button"
              onClick={() => setConfigureOpen(true)}
              className="px-3 py-1.5 text-xs font-semibold rounded border border-line bg-white hover:bg-parchment transition-colors"
            >
              Configure roles
            </button>
          )}
        </div>
      </div>

      {/* Awaiting-confirmation banner — placed-but-unconfirmed duties are invisible
          to volunteers until confirmed. Shown above every sub-tab so it can't be missed. */}
      {fullManager && assignedAssignments.length > 0 && (
        <div data-print-hide style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', background: EV.brassSoft, border: '1.5px solid #e3d6a8', borderRadius: 12, padding: '12px 16px' }}>
          <span style={{ width: 38, height: 38, borderRadius: 10, background: '#fff', border: '1px solid #e3d6a8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="bell" size={19} color="#9b7f2a" /></span>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontFamily: EV.serif, fontSize: 15.5, fontWeight: 700, color: '#6b560c' }}>{assignedAssignments.length} {assignedAssignments.length === 1 ? 'duty is' : 'duties are'} placed but not yet confirmed</div>
            <div style={{ fontFamily: EV.body, fontSize: 12.5, color: '#6b560c', marginTop: 1 }}>Placed volunteers can't see their duty in InfoHub until you confirm. Confirming notifies them and lights up their Events Live banner.</div>
          </div>
          <button onClick={confirmAllAssigned} disabled={confirmVolMut.isPending} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: EV.green, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontFamily: EV.body, fontSize: 13.5, fontWeight: 800, cursor: confirmVolMut.isPending ? 'not-allowed' : 'pointer', flexShrink: 0, opacity: confirmVolMut.isPending ? 0.7 : 1 }}>
            <Icon name="check" size={15} color="#fff" strokeWidth={2.4} /> {confirmVolMut.isPending ? 'Confirming…' : `Confirm & notify all${assignedUserIds.length > 1 ? ` (${assignedUserIds.length})` : ''}`}
          </button>
        </div>
      )}

      {!fullManager ? (
        <div className="space-y-3">
          {isAdmin && !isDesktop && (
            <div
              className="flex items-start gap-3 rounded-lg border px-4 py-3 text-sm"
              style={{ background: EV.brassSoft, borderColor: '#e3d6a8', color: '#6b560c' }}
            >
              <Icon name="info" size={18} color="#9b7f2a" />
              <div>
                <div className="font-semibold">Roster management is available on a larger screen</div>
                <div className="text-xs mt-0.5">Drag-and-drop rostering needs a desktop. This is a read-only view of the rota.</div>
              </div>
            </div>
          )}
          <ReadOnlyGrid roles={roles} days={days} byCell={ctx.byCell} signedOn={ctx.signedOn} />
        </div>
      ) : (
        <div className="bg-white border border-line rounded overflow-hidden" data-roster-grid-card>
          {/* sub-tab strip */}
          <div className="border-b px-4 pt-3" style={{ borderColor: EV.line }}>
            <div className="flex gap-1.5 flex-wrap">
              {SUBTABS.map((t) => {
                const on = t.key === sub
                return (
                  <button
                    key={t.key}
                    onClick={() => setSub(t.key)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] cursor-pointer"
                    style={{
                      borderRadius: '8px 8px 0 0', border: 'none',
                      borderBottom: on ? `2.5px solid ${EV.green}` : '2.5px solid transparent',
                      background: on ? EV.parchment : 'transparent',
                      color: on ? EV.green : EV.inkSoft, fontWeight: on ? 800 : 600
                    }}
                  >
                    <Icon name={t.icon} size={15} color={on ? EV.green : EV.inkSoft} strokeWidth={on ? 2.1 : 1.8} /> {t.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="px-5 pt-2 pb-1 flex items-center gap-2">
            <Icon name="info" size={14} color={EV.inkSoft} />
            <span style={{ fontFamily: EV.body, fontSize: 12.5, color: EV.inkSoft }}>{cur.hint}</span>
          </div>
          <div className="el-scroll px-5 pt-2 pb-6 overflow-auto" style={{ background: EV.parchment }}>
            {offersQ.isLoading && (sub === 'availability') ? (
              <div className="text-ink-soft text-sm font-mono py-4">Loading offers…</div>
            ) : (
              <View ctx={ctx} />
            )}
          </div>
        </div>
      )}

      {!archived && configureOpen && (
        <ConfigureRolesModal eventId={eventId} onClose={() => setConfigureOpen(false)} />
      )}
    </div>
  )
}

// ---- Read-only grid (non-admin + narrow fallback) ------------------------

function ReadOnlyGrid({
  roles, days, byCell, signedOn
}: {
  roles: LibraryRole[]
  days: string[]
  byCell: (roleId: number, date: string) => RosterAssignment[]
  signedOn: (userId: number) => boolean
}) {
  if (days.length === 0) {
    return <p className="text-xs italic text-ink-soft">No operating days — check the event start/end dates.</p>
  }
  if (roles.length === 0) {
    return <p className="text-xs italic text-ink-soft">No roles configured for this event yet.</p>
  }
  return (
    <div className="bg-white border border-line rounded p-4 overflow-x-auto">
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `minmax(180px, 1fr) repeat(${days.length}, minmax(130px, 1fr))`, minWidth: 180 + days.length * 130 }}
      >
        <div className="px-3 py-2 text-xs uppercase tracking-wide text-ink-soft font-semibold flex items-end">Role</div>
        {days.map((d) => {
          const date = parseIsoDate(d)
          return (
            <div key={d} className="px-2 py-2 text-center bg-parchment border border-line rounded">
              <div className="text-[10px] uppercase tracking-wide text-ink-soft">{WEEKDAY_SHORT[date.getUTCDay()]}</div>
              <div className="font-mono text-xs font-semibold mt-0.5">{date.getUTCDate()} {MONTH_SHORT[date.getUTCMonth()]}</div>
            </div>
          )
        })}
        {roles.map((role) => (
          <Fragment key={role.id}>
            <div className="px-3 py-2 bg-parchment border border-line rounded text-sm font-semibold">{role.name}</div>
            {days.map((d) => {
              const chips = byCell(role.id, d)
              return (
                <div key={`${role.id}|${d}`} className="px-2 py-1.5 rounded border border-line bg-white min-h-[2.5rem] flex items-center flex-wrap gap-1">
                  {chips.length === 0
                    ? <span className="text-xs italic text-ink-soft/60 mx-auto">—</span>
                    : chips.map((c) => (
                      <span key={c.id} className="inline-flex items-center gap-1 rounded-full bg-parchment border border-line pl-0.5 pr-2 py-0.5">
                        {signedOn(c.userId)
                          ? <span title="On duty now" style={{ width: 8, height: 8, borderRadius: '50%', background: EV.live, animation: 'el-pulse 1.8s infinite' }} />
                          : <StatusDot status={c.status} />}
                        <EvAvatar name={c.userName} size={18} />
                        <span className="text-xs">{firstName(c.userName)}</span>
                      </span>
                    ))}
                </div>
              )
            })}
          </Fragment>
        ))}
      </div>
    </div>
  )
}

// ---- Configure Roles modal (with per-day need) ---------------------------

function ConfigureRolesModal({ eventId, onClose }: { eventId: number; onClose: () => void }) {
  const { data, isLoading, error } = useEventRoles(eventId)
  const setMut = useSetEventRoles(eventId)
  const [selected, setSelected] = useState<Set<number> | null>(null)
  const [needs, setNeeds] = useState<Record<number, string>>({})

  useEffect(() => {
    if (data && selected === null) {
      setSelected(new Set(data.filter((r) => r.inUse).map((r) => r.role.id)))
      const seed: Record<number, string> = {}
      for (const r of data) if (r.needPerDay != null) seed[r.role.id] = String(r.needPerDay)
      setNeeds(seed)
    }
  }, [data, selected])

  if (isLoading || selected === null) {
    return <ModalShell title="Configure roles" onClose={onClose} size="xl" body={<div className="text-ink-soft text-sm font-mono">Loading roles…</div>} />
  }
  if (error) {
    return (
      <ModalShell title="Configure roles" onClose={onClose} size="xl" body={
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load roles</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      } />
    )
  }
  if (!data) return null

  function toggle(roleId: number) {
    setSelected((prev) => {
      if (prev === null) return prev
      const next = new Set(prev)
      if (next.has(roleId)) next.delete(roleId); else next.add(roleId)
      return next
    })
  }
  function handleSave() {
    if (selected === null) return
    const roleIds = Array.from(selected)
    const needByRole: Record<string, number | null> = {}
    for (const id of roleIds) {
      const raw = needs[id]
      needByRole[String(id)] = raw != null && raw !== '' ? Math.max(0, Math.floor(Number(raw))) : null
    }
    setMut.mutate({ roleIds, needByRole }, { onSuccess: () => onClose() })
  }

  const saving = setMut.isPending
  const serverError = setMut.error as Error | null

  return (
    <ModalShell
      title="Configure roles"
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); handleSave() }}
      size="2xl"
      footer={<><CancelButton onClick={onClose} disabled={saving} /><PrimaryButton disabled={saving}>{saving ? 'Saving…' : 'Save'}</PrimaryButton></>}
      body={
        <div className="space-y-4">
          <p className="text-sm text-ink-soft">
            Tick the roles for this event's roster, and set how many of each you need per day
            (drives the Coverage heat-map and the “{'{need}'} needed / day” copy volunteers see).
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={() => setSelected(new Set(data.map((r) => r.role.id)))} className="text-xs font-semibold px-3 py-1 rounded border border-line hover:bg-parchment transition-colors">Select all</button>
            <button type="button" onClick={() => setSelected(new Set())} className="text-xs font-semibold px-3 py-1 rounded border border-line hover:bg-parchment transition-colors">Select none</button>
            <span className="text-xs text-ink-soft font-mono ml-auto flex items-center">{selected.size} / {data.length} selected</span>
          </div>
          <div className="border border-line rounded divide-y divide-line max-h-[480px] overflow-y-auto">
            {data.map(({ role }) => {
              const checked = selected.has(role.id)
              return (
                <div key={role.id} className="flex items-start gap-3 px-3 py-2.5 hover:bg-parchment/40 transition-colors">
                  <input type="checkbox" checked={checked} onChange={() => toggle(role.id)} disabled={saving} className="w-4 h-4 mt-0.5 accent-green cursor-pointer disabled:cursor-not-allowed" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-semibold">{role.name}</span>
                      <span className="text-[10px] text-ink-soft font-mono">{role.slug}</span>
                      <span className="text-[10px] uppercase tracking-wide text-ink-soft">{role.roleType}</span>
                    </div>
                    {role.description && <div className="text-xs text-ink-soft mt-0.5 line-clamp-2">{role.description}</div>}
                  </div>
                  {checked && (
                    <label className="flex items-center gap-1.5 text-xs text-ink-soft flex-shrink-0">
                      <span>need/day</span>
                      <input
                        type="number" min={0} max={99}
                        value={needs[role.id] ?? ''}
                        onChange={(e) => setNeeds((n) => ({ ...n, [role.id]: e.target.value }))}
                        disabled={saving}
                        className="w-14 px-2 py-1 rounded border border-line text-sm"
                        placeholder="—"
                      />
                    </label>
                  )}
                </div>
              )
            })}
            {data.length === 0 && (
              <div className="px-3 py-4 text-sm italic text-ink-soft text-center">
                No active roles in the catalogue. Add some from the Roles library first.
              </div>
            )}
          </div>
          {serverError && <ServerError title="Could not save" error={serverError} />}
        </div>
      }
    />
  )
}

// ---- CSV export (preserved verbatim from the previous build) --------------

function exportRosterCsv(
  event: EventListItem,
  roles: LibraryRole[],
  days: string[],
  assignments: RosterAssignment[]
) {
  const byCell = new Map<string, RosterAssignment[]>()
  for (const a of assignments) {
    const key = `${a.roleId}|${a.assignmentDate}`
    const list = byCell.get(key) ?? []
    list.push(a)
    byCell.set(key, list)
  }
  for (const list of byCell.values()) list.sort((x, y) => x.sortOrder - y.sortOrder)

  const headerRow = ['Role', ...days.map(formatDateForCsv)]
  const bodyRows = roles.map((role) => {
    const row = [role.name]
    for (const d of days) {
      const chips = byCell.get(`${role.id}|${d}`) ?? []
      row.push(chips.map((c) => chipToCsvString(c, role)).join(' / '))
    }
    return row
  })
  const lines = [headerRow, ...bodyRows].map((row) => row.map(csvEscape).join(',')).join('\n')

  const blob = new Blob([lines], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `nymr-roster-${slugify(event.name)}-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function chipToCsvString(chip: RosterAssignment, role: LibraryRole): string {
  const hasTime = chip.timeStart !== null || chip.timeEnd !== null
  const overrides = hasTime && (chip.timeStart !== role.defaultShiftStart || chip.timeEnd !== role.defaultShiftEnd)
  const name = chip.userName ?? '(unnamed)'
  const time = overrides ? ` (${chip.timeStart ?? '?'}–${chip.timeEnd ?? '?'})` : ''
  const note = chip.note ? ` · ${chip.note}` : ''
  const status = chip.status === 'confirmed' ? '' : ' [unconfirmed]'
  return `${name}${time}${note}${status}`
}

function formatDateForCsv(iso: string): string {
  const d = parseIsoDate(iso)
  return `${WEEKDAY_SHORT[d.getUTCDay()] ?? ''} ${d.getUTCDate()} ${MONTH_SHORT[d.getUTCMonth()] ?? ''}`
}
function csvEscape(s: string): string {
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`
  return s
}
function slugify(s: string): string {
  return s.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)
}
